/**
 * Green Flag — EventArrivalInfo
 *
 * What an attendee needs when they get to the venue: how to find the group and
 * where to park. The same two details the reminder email and text carry, from
 * the venue's CMS entry, which editors write in markdown. The API only sends
 * `arrival` when at least one is set, so callers mount this on
 * `registration.arrival &&`.
 */

import { Box, Flex, Text } from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPeopleGroup,
  faSquareParking,
} from "@fortawesome/free-solid-svg-icons";
import { SparkArrivalInfo } from "@greenflagdate/shared";
import { StandardMarkdown } from "../StandardMarkdown/StandardMarkdown";

export interface EventArrivalInfoProps {
  arrival: SparkArrivalInfo;
}

export function EventArrivalInfo({ arrival }: EventArrivalInfoProps) {
  const rows = [
    {
      icon: faPeopleGroup,
      label: "How to find the group",
      value: arrival.find_group_information,
    },
    {
      icon: faSquareParking,
      label: "Parking",
      value: arrival.parking_information,
    },
  ].filter((row): row is typeof row & { value: string } => !!row.value);

  return (
    <Flex direction="column" gap="1rem">
      {rows.map((row) => (
        <Flex key={row.label} gap="0.875rem" align="flex-start">
          <Flex
            w="1.25rem"
            flexShrink={0}
            justify="center"
            pt="0.125rem"
            color="greenDark.solid"
          >
            <FontAwesomeIcon icon={row.icon} />
          </Flex>
          <Box>
            <Text textStyle="labelStrong">{row.label}</Text>
            <StandardMarkdown
              content={row.value}
              // A single line break in the CMS still breaks the line, as it
              // did before this text was read as markdown.
              lineBreaks
              color="fg.muted"
              mt="0.125rem"
              // List items take the row's muted color like the paragraphs
              // (StandardMarkdown gives them their own), and the last block
              // leaves no gap under the row.
              css={{
                "& li": { color: "inherit" },
                "& > :last-child": { marginBottom: 0 },
              }}
            />
          </Box>
        </Flex>
      ))}
    </Flex>
  );
}
